import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { InstitutionItemTemplatesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionActions from "../../../actions/institution";
import * as InstitutionItemTemplateActions from "../../../actions/institutionItemTemplate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput, SelectFormInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { InstitutionLookupsLayout } from "../../index";

import "./InstitutionItemTemplatesPage.scss";

interface InstitutionItemTemplatesPageProps {
    institutions: Dtos.Institution[];
    loadingInstitutions: boolean;
    loadInstitutionsSuccess: boolean;
    loadInstitutionsFailure: boolean;
    loadInstitutionsErrorCode: string;
    institutionItemTemplates: Dtos.InstitutionItemTemplate[];
    institutionItemsByCategory: Dtos.InstitutionItemTemplateCategory[];
    formFilter: IFormFilter;
    lookups: Dtos.Lookup[];
    loadingInstitutionItemTemplates: boolean;
    loadInstitutionItemTemplatesSuccess: boolean;
    loadInstitutionItemTemplatesFailure: boolean;
    loadInstitutionItemTemplatesErrorCode: string;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearInstitutions: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitutions: InstitutionActions.IInstitutionLoadActionCreator;
    clearInstitutionItemTemplates: InstitutionItemTemplateActions.IInstitutionItemTemplateClearActionCreator;
    loadInstitutionItemTemplates: InstitutionItemTemplateActions.IInstitutionItemTemplateLoadActionCreator;
    filter: InstitutionItemTemplateActions.IInstitutionItemTemplateFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const InstitutionItemTemplatesPage: FC<InstitutionItemTemplatesPageProps> = ({
    institutions,
    loadingInstitutions,
    loadInstitutionsFailure,
    loadInstitutionsErrorCode,
    institutionItemTemplates,
    institutionItemsByCategory,
    formFilter,
    lookups,
    loadingInstitutionItemTemplates,
    loadInstitutionItemTemplatesSuccess,
    loadInstitutionItemTemplatesFailure,
    loadInstitutionItemTemplatesErrorCode,
    permissions,
    clearInstitutions,
    loadInstitutions,
    clearInstitutionItemTemplates,
    loadInstitutionItemTemplates,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{
        institutionId: string;
    }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- States --
    const [institutionIdState, setInstitutionIdState] = useState<number | null>(
        institutionIdNumber
    );
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const loading = loadingInstitutionItemTemplates || loadingInstitutions;
    const showInstitutionItems =
        !loadingInstitutionItemTemplates &&
        loadInstitutionItemTemplatesSuccess &&
        !creatingTree;


    // Load Institutions, Institutions & Items
    useEffect(() => {
        setDrawerItems(SettingsInstitutionsSubMenu(permissions));
        setBreadcrumbs(
            InstitutionItemTemplatesBreadcrumbs(
                isNaN(institutionIdNumber) ? undefined : institutionIdNumber
            )
        );

        // Dropdown options
        if (!institutions || institutions.length === 0) {
            loadInstitutions();
        }

        // Load templates
        if (
            institutionIdState &&
            !loadingInstitutionItemTemplates &&
            !loadInstitutionItemTemplatesSuccess
        ) {
            loadInstitutionItemTemplates(institutionIdState);
        }
    }, [
        loadInstitutions,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        setBreadcrumbs,
        InstitutionItemTemplatesBreadcrumbs,
        institutions,
        institutionIdState,
        institutionItemTemplates,
        permissions,
        loadInstitutionItemTemplatesSuccess,
        loadInstitutionItemTemplates,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionsFailure) {
            navigate(`/error/${loadInstitutionsErrorCode}`);
        }
        if (loadInstitutionItemTemplatesFailure) {
            navigate(`/error/${loadInstitutionItemTemplatesErrorCode}`);
        }
    }, [
        loadInstitutionsFailure,
        navigate,
        loadInstitutionItemTemplatesFailure,
        loadInstitutionsErrorCode,
        loadInstitutionItemTemplatesErrorCode,
    ]);

    // Cleanup effect on component unmount
    useEffect(() => {
        return () => {
            clearInstitutions();
            clearInstitutionItemTemplates();
        };
    }, [clearInstitutions, clearInstitutionItemTemplates]);

    // Dropdown options memos
    const institutionSelectOptions = useMemo(() => {
        if (!institutions) return [];

        return institutions
            ?.filter((x) => x.name)
            ?.sort((a, b) => a?.name.localeCompare(b?.name))
            ?.map((x) => ({ value: x.id, label: x?.name }));
    }, [institutions]);

    // Reset tree initalisation on form filter change
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [
        institutionItemsByCategory,
        formFilter,
        setInitalizedTreeData,
        institutionId,
    ]);

    // Create tree
    useEffect(() => {
        if (institutionItemsByCategory && !initalizedTreeData) {
            setCreatingTree(true);

            // If no institution items return
            if (
                !institutionItemsByCategory ||
                institutionItemsByCategory?.length == 0
            ) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (institutionItems) => {
                let children = [];

                children =
                    institutionItems
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 4fr 4fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.FILE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <span
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </span>
                                    </div>
                                    <div className="px-1">
                                        {y?.initialVersion}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 4fr 4fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Initial Version</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                institutionItemsByCategory
                    ?.sort((a, b) => a?.rank - b?.rank)
                    ?.map((x) => ({
                        id: `category-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "400px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {x?.institutionItemTemplates?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [
                                ...createChildNodes(x.institutionItemTemplates),
                            ],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        institutionItemsByCategory,
        initalizedTreeData,
        treeData,
        formFilter,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("item") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.InstitutionItemView
                    )
                ) {
                    navigate(
                        institutionIdState
                            ? `/settings/institution/itemtemplates/${institutionIdState}/template/${id.replace(
                                  "item-",
                                  ""
                              )}`
                            : `/settings/institution/itemtemplates/template/${id.replace(
                                  "item-",
                                  ""
                              )}`
                    );
                }
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions]
    );

    const pageContent = useMemo(() => {
        return (
            <>
                <div className="row">
                    <div className={"col-md-12 col-lg-auto mb-2"}>
                        <h1>Institution Item Templates</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-md-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        <div
                            className="d-inline-table pr-2"
                            style={{ width: "400px" }}
                        >
                            <SelectFormInput
                                loading={loadingInstitutions}
                                multi={false}
                                options={institutionSelectOptions}
                                clearable={true}
                                backspaceRemoves={false}
                                name={"institutionIdState"}
                                id={"institutionIdState"}
                                placeholder="Institution"
                                onChange={function (
                                    value: any,
                                    _options?: any
                                ): any {
                                    clearInstitutionItemTemplates();
                                    setInstitutionIdState(
                                        value != undefined
                                            ? parseInt(value)
                                            : null
                                    );
                                    return undefined;
                                }}
                                value={institutionIdState}
                            />
                        </div>
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InstitutionItemCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    institutionIdState
                                        ? `/settings/institution/itemtemplates/${institutionIdState}/template/create`
                                        : "/settings/institution/itemtemplates/template/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {loading ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {!institutionIdState ? (
                    <div className="card card-shadow p-3">
                        <div className="patient-tables">
                            <div className="alert alert-info d-flex mb-0">
                                <div className="mr-2">
                                    <FontAwesomeIcon
                                        icon={
                                            FontAwesomeIcons.Solid.INFO_SQUARE
                                        }
                                        fixedWidth
                                    />
                                </div>
                                <span>
                                    Select a institution to view item templates.
                                </span>
                            </div>
                        </div>
                    </div>
                ) : null}
                {showInstitutionItems && institutionIdState && !loading ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </>
        );
    }, [
        generateOptionsFromLookup,
        findLookup,
        lookups,
        showInstitutionItems,
        institutionIdState,
        setInstitutionIdState,
        institutionItemTemplates,
        institutionSelectOptions,
        loadingInstitutions,
        clearInstitutionItemTemplates,
        treeData,
        handleToggle,
        toggleState,
        loading,
    ]);

    return (
        <InstitutionLookupsLayout
            permissions={permissions}
            permission={Dtos.Permission.InstitutionItemView}
        >
            {pageContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionItemTemplatesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutions: state.institutions.formFilteredData,
        loadingInstitutions:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionsSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionsFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionsErrorCode: state.institutions.loadState.errorCode,
        institutionItemTemplates:
            state.institutionItemTemplate.formFilteredData!,
        institutionItemsByCategory:
            state.institutionItemTemplate.formFilteredDataGrouped,
        formFilter: state.institutionItemTemplate.formFilter,
        lookups: state.institutionItemTemplate.lookups,
        loadingInstitutionItemTemplates:
            state.institutionItemTemplate.loadState &&
            state.institutionItemTemplate.loadState.status ==
                RequestState.Pending,
        loadInstitutionItemTemplatesSuccess:
            state.institutionItemTemplate.loadState &&
            state.institutionItemTemplate.loadState.status ==
                RequestState.Success,
        loadInstitutionItemTemplatesFailure:
            state.institutionItemTemplate.loadState &&
            state.institutionItemTemplate.loadState.status ==
                RequestState.Failure,
        loadInstitutionItemTemplatesErrorCode:
            state.institutionItemTemplate.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionItemTemplatesPageProps> => {
    return {
        loadInstitutions: bindActionCreators(
            InstitutionActions.LoadInstitution,
            dispatch
        ),
        clearInstitutions: bindActionCreators(
            InstitutionActions.Clear,
            dispatch
        ),
        loadInstitutionItemTemplates: bindActionCreators(
            InstitutionItemTemplateActions.LoadInstitutionItemTemplate,
            dispatch
        ),
        clearInstitutionItemTemplates: bindActionCreators(
            InstitutionItemTemplateActions.Clear,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        filter: bindActionCreators(
            InstitutionItemTemplateActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionItemTemplatesPage);
